import React, { useState, useEffect } from "react";
import {
  X,
  CheckCircle,
  FileText,
  Calendar,
  User,
  Wallet,
  Receipt,
  Package,
  MessageSquare,
  Printer,
  Info,
  Loader2,
} from "lucide-react";
import { executePrint } from "../../utils/printManager";
import { returnService } from "../../api/returnService";
import { useNotification } from "../../contexts/NotificationContext";

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const formatDateIndo = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const PAYMENT_LABELS = {
  CASH: "Tunai",
  TRANSFER: "Transfer",
  QRIS: "QRIS",
  MIXED: "Campuran",
};

export default function ReturnSuccessModal({ returnData, onClose, onPrint }) {
  const [data, setData] = useState(returnData);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    setData(returnData);
    if (returnData?.id && (!returnData?.items || returnData.items.length === 0)) {
      setLoading(true);
      returnService
        .getReturnDetail(returnData.id)
        .then((res) => {
          const detail = res?.data ?? res;
          if (detail) setData(detail);
        })
        .catch((e) => console.error("Gagal memuat rincian retur:", e))
        .finally(() => setLoading(false));
    }
  }, [returnData]);

  if (!data) return null;

  const returnNumber = data.return_number || "RTR-";
  const refTransactionNumber = data.transaction_number || data.ref_transaction_number || "-";
  const returnDate = formatDateIndo(data.return_date || data.created_at);
  const customerName = data.customer_name || "Pelanggan Umum";
  const refundMethod = PAYMENT_LABELS[data.refund_method] || data.refund_method || "Tunai";
  const reason = data.reason || "-";
  const totalRefund = data.total_refund_amount || 0;
  const items = data.items || [];
  const totalStockRestored = data.total_stock_restored || items.reduce((acc, itm) => acc + Number(itm.quantity || 0), 0);

  const handlePrintReceipt = () => {
    if (onPrint) {
      onPrint(data);
      return;
    }
    executePrint(data, "return")
      .then((res) => {
        if (res?.method === "bluetooth") {
          showSuccess("Bukti retur berhasil dikirim ke printer bluetooth");
        } else if (res?.method === "usb") {
          showSuccess("Bukti retur berhasil dikirim ke printer USB");
        } else if (res?.method === "serial") {
          showSuccess("Bukti retur berhasil dikirim ke printer Serial");
        } else if (res?.method === "windows") {
          showSuccess("Bukti retur berhasil dikirim ke printer Windows");
        }
      })
      .catch((err) => {
        showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
      });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* === HEADER === */}
        <div className="px-6 py-5 bg-[#0f766e] text-white flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-white">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-wider text-teal-100 uppercase">
                RETUR BERHASIL
              </p>
              <h2 className="text-xl font-bold tracking-tight text-white">
                {returnNumber}
              </h2>
            </div>
            <span className="ml-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/20 text-emerald-100 ring-1 ring-emerald-400/40 flex items-center gap-1">
              ✓ Berhasil Diproses
            </span>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-teal-100 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* === BODY === */}
        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto bg-slate-50/50">
          {/* Referensi Nota Banner */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-teal-50/70 border border-teal-200/80 rounded-xl text-teal-900 text-sm">
            <FileText className="w-4 h-4 text-teal-600 flex-shrink-0" />
            <span className="font-medium">Referensi Nota:</span>
            <span className="font-bold text-teal-800">{refTransactionNumber}</span>
          </div>

          {/* Meta 4 Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                <Calendar className="w-3 h-3 text-slate-400" />
                TANGGAL RETUR
              </p>
              <p className="text-xs sm:text-sm font-semibold text-slate-800 truncate">
                {returnDate}
              </p>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                <User className="w-3 h-3 text-slate-400" />
                PELANGGAN
              </p>
              <p className="text-xs sm:text-sm font-semibold text-slate-800 truncate">
                {customerName}
              </p>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                <Wallet className="w-3 h-3 text-slate-400" />
                METODE BAYAR
              </p>
              <p className="text-xs sm:text-sm font-semibold text-slate-800 truncate">
                {refundMethod}
              </p>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                <Receipt className="w-3 h-3 text-slate-400" />
                REFERENSI NOTA
              </p>
              <p className="text-xs sm:text-sm font-semibold text-slate-800 truncate">
                {refTransactionNumber}
              </p>
            </div>
          </div>

          {/* Rincian Pengembalian Section */}
          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-3">
            <h3 className="text-xs font-bold text-teal-800 uppercase tracking-wider flex items-center gap-1.5">
              <Package className="w-4 h-4 text-teal-600" />
              RINCIAN PENGEMBALIAN
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 text-[11px] font-semibold text-left">
                    <th className="pb-2 font-semibold">PRODUK</th>
                    <th className="pb-2 text-center font-semibold">QTY RETUR</th>
                    <th className="pb-2 text-right font-semibold">NILAI PENGEMBALIAN</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item, idx) => (
                    <tr key={idx} className="text-slate-800">
                      <td className="py-2.5 font-medium flex items-center gap-2">
                        <Package className="w-4 h-4 text-teal-600 flex-shrink-0" />
                        <span>{item.item_name}</span>
                      </td>
                      <td className="py-2.5 text-center font-semibold text-slate-700">
                        {item.quantity} {item.unit_name || "PCS"}
                      </td>
                      <td className="py-2.5 text-right font-bold text-slate-900">
                        Rp {fmt(item.total_price ?? (item.unit_price * item.quantity))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Alasan Retur */}
            <div className="pt-2 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-600">
              <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold text-slate-700 uppercase tracking-wide text-[10px] block">
                  ALASAN RETUR
                </span>
                <span className="text-slate-800">{reason}</span>
              </div>
            </div>
          </div>

          {/* Ringkasan Retur Section */}
          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-3">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <Receipt className="w-4 h-4 text-slate-500" />
              RINGKASAN RETUR
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50/80 rounded-xl border border-slate-100">
                <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-700">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-500">
                    Pengembalian Dana
                  </p>
                  <p className="text-base sm:text-lg font-bold text-slate-900">
                    Rp {fmt(totalRefund)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-emerald-800">
                    Stok Bertambah
                  </p>
                  <p className="text-base sm:text-lg font-bold text-emerald-600">
                    +{totalStockRestored} PCS
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Alert Info Box */}
          <div className="flex items-center gap-2 px-4 py-3 bg-sky-50 border border-sky-200/80 rounded-xl text-sky-800 text-xs font-medium">
            <Info className="w-4 h-4 text-sky-600 flex-shrink-0" />
            <span>
              Dicatat pada laporan tanggal retur, tanpa mengubah transaksi penjualan asli.
            </span>
          </div>
        </div>

        {/* === FOOTER === */}
        <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors"
          >
            Tutup
          </button>

          <button
            onClick={handlePrintReceipt}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0f766e] hover:bg-[#0d665f] text-white text-xs font-semibold shadow-sm shadow-teal-700/20 transition-all active:scale-[0.98]"
          >
            <Printer className="w-4 h-4" />
            Cetak Bukti Retur
          </button>
        </div>
      </div>
    </div>
  );
}
